// Toggleable dropdown panels.
//
// HTML contract:
//   <div class="bf-dropdown" data-bf-dropdown>
//     <button data-bf-toggle="dropdown" aria-expanded="false">…</button>
//     <div class="bf-dropdown__panel" role="menu">…</div>
//   </div>
//
// Closes on outside click, Escape, or when an item is clicked (unless
// data-bf-keepopen is set on the item).

import { $$, on, type Cleanup } from './util';

const SEL_ROOT = '.bf-dropdown';
const SEL_TOGGLE = '[data-bf-toggle="dropdown"]';
const SEL_PANEL = '.bf-dropdown__panel';

function close(root: HTMLElement): void {
  root.dataset.bfOpen = 'false';
  const t = root.querySelector(SEL_TOGGLE) as HTMLElement | null;
  if (t) t.setAttribute('aria-expanded', 'false');
}

function open(root: HTMLElement): void {
  for (const other of $$<HTMLElement>(`${SEL_ROOT}[data-bf-open="true"]`)) if (other !== root) close(other);
  root.dataset.bfOpen = 'true';
  const t = root.querySelector(SEL_TOGGLE) as HTMLElement | null;
  if (t) t.setAttribute('aria-expanded', 'true');
  const firstItem = root.querySelector('.bf-dropdown__item') as HTMLElement | null;
  firstItem?.focus({ preventScroll: true });
}

export function toggle(root: HTMLElement): void {
  if (root.dataset.bfOpen === 'true') close(root);
  else open(root);
}

export function attach(root: HTMLElement): Cleanup {
  const handlers: Cleanup[] = [];

  handlers.push(on(root, 'click', (e) => {
    const target = e.target as HTMLElement;
    const t = target.closest(SEL_TOGGLE);
    if (t && root.contains(t)) {
      e.preventDefault();
      toggle(root);
      return;
    }
    const item = target.closest('.bf-dropdown__item') as HTMLElement | null;
    if (item && !item.hasAttribute('data-bf-keepopen')) close(root);
  }));

  handlers.push(on(root, 'keydown', (e) => {
    const k = (e as KeyboardEvent).key;
    if (k === 'Escape') { close(root); (root.querySelector(SEL_TOGGLE) as HTMLElement | null)?.focus(); }
    if (k === 'ArrowDown' || k === 'ArrowUp') {
      const items = $$<HTMLElement>('.bf-dropdown__item', root.querySelector(SEL_PANEL) as HTMLElement | undefined ?? root);
      if (items.length === 0) return;
      const i = items.findIndex(it => it === document.activeElement);
      const next = k === 'ArrowDown' ? (i + 1 + items.length) % items.length : (i - 1 + items.length) % items.length;
      items[next]!.focus();
      e.preventDefault();
    }
  }));

  return () => handlers.forEach(c => c());
}

export function init(root: ParentNode = document): void {
  for (const el of $$<HTMLElement>(SEL_ROOT, root)) attach(el);
}

// Global outside-click + escape handling, registered once.
let globalsBound = false;
export function bindGlobals(): void {
  if (globalsBound) return;
  globalsBound = true;
  document.addEventListener('click', (e) => {
    const t = e.target as Node;
    for (const root of $$<HTMLElement>(`${SEL_ROOT}[data-bf-open="true"]`)) {
      if (!root.contains(t)) close(root);
    }
  });
}
